import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { MapPin, Truck } from 'lucide-react';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { getStatusLabel, getStatusMarkerColor } from '../services/ServiceStatusBadge';
import { hasValidCoords, normalizeCoords } from '../../utils/locationCoords';

const MAP_HEIGHT = 520;

const STALE_LOCATION_MS = 10 * 60 * 1000;

function isLocationStale(recordedAt) {
    if (!recordedAt) return true;
    const ts = new Date(recordedAt).getTime();
    if (Number.isNaN(ts)) return true;
    return Date.now() - ts > STALE_LOCATION_MS;
}

const LiveJobsMap = ({
    jobs,
    driverLocations,
    statusCatalog,
    selectedJobId,
    selectedJob,
    onSelectJob,
}) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);
    const hasFitOnceRef = useRef(false);
    const { isLoaded, loading, error } = useGoogleMaps();

    const jobsOnMap = useMemo(
        () => jobs.filter((job) => hasValidCoords(job.pickup_location)),
        [jobs],
    );

    const driversOnMap = useMemo(
        () => driverLocations.filter((entry) => hasValidCoords(entry?.location)),
        [driverLocations],
    );

    const legendItems = useMemo(() => {
        const usedStatuses = [...new Set(jobsOnMap.map((j) => j.status))];
        return usedStatuses.slice(0, 6).map((code) => ({
            code,
            label: getStatusLabel(code, statusCatalog),
            color: getStatusMarkerColor(code, statusCatalog),
        }));
    }, [jobsOnMap, statusCatalog]);

    const clearMarkers = useCallback(() => {
        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = [];
    }, []);

    const fitToMarkers = useCallback((padding = 64) => {
        if (!mapRef.current || !window.google?.maps) return;
        const bounds = new window.google.maps.LatLngBounds();
        let count = 0;

        markersRef.current.forEach((marker) => {
            const pos = marker.getPosition();
            if (pos) {
                bounds.extend(pos);
                count += 1;
            }
        });

        if (count === 1) {
            mapRef.current.setCenter(bounds.getCenter());
            mapRef.current.setZoom(14);
        } else if (count > 1) {
            mapRef.current.fitBounds(bounds, padding);
        }
    }, []);

    const panToJob = useCallback(
        (job) => {
            if (!mapRef.current || !job) return;
            const google = window.google;
            if (!google?.maps) return;

            const bounds = new google.maps.LatLngBounds();
            let hasPoint = false;

            const pickup = normalizeCoords(job.pickup_location);
            if (pickup) {
                bounds.extend({ lat: pickup.latitude, lng: pickup.longitude });
                hasPoint = true;
            }
            const driverLoc = normalizeCoords(job.driver_location);
            if (driverLoc) {
                bounds.extend({ lat: driverLoc.latitude, lng: driverLoc.longitude });
                hasPoint = true;
            }
            if (hasPoint) {
                if (pickup && !driverLoc) {
                    mapRef.current.setCenter({ lat: pickup.latitude, lng: pickup.longitude });
                    mapRef.current.setZoom(15);
                } else {
                    mapRef.current.fitBounds(bounds, 80);
                }
            }
        },
        [],
    );

    useEffect(() => {
        if (!isLoaded || !mapContainerRef.current || mapRef.current) return;

        const google = window.google;
        if (!google?.maps) return;

        mapRef.current = new google.maps.Map(mapContainerRef.current, {
            center: { lat: -41.2865, lng: 174.7762 },
            zoom: 6,
            mapTypeControl: true,
            mapTypeControlOptions: { position: google.maps.ControlPosition.TOP_LEFT },
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
        });
        infoWindowRef.current = new google.maps.InfoWindow();
    }, [isLoaded]);

    useEffect(() => {
        if (!isLoaded || !mapRef.current || !window.google?.maps) return;

        const google = window.google;
        clearMarkers();

        jobsOnMap.forEach((job) => {
            const coords = normalizeCoords(job.pickup_location);
            if (!coords) return;

            const color =
                job.sla_state === 'breached'
                    ? '#DC2626'
                    : job.sla_state === 'at_risk'
                      ? '#F59E0B'
                      : getStatusMarkerColor(job.status, statusCatalog);
            const isSelected = selectedJobId === job.id;

            const marker = new google.maps.Marker({
                map: mapRef.current,
                position: { lat: coords.latitude, lng: coords.longitude },
                title: job.service_number,
                label: isSelected
                    ? {
                          text: '●',
                          color: '#ffffff',
                          fontSize: '10px',
                          fontWeight: 'bold',
                      }
                    : undefined,
                zIndex: isSelected ? 1000 : 100,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: isSelected ? 12 : 9,
                    fillColor: color,
                    fillOpacity: 1,
                    strokeColor: isSelected ? '#0f172a' : '#ffffff',
                    strokeWeight: isSelected ? 3 : 2,
                },
            });

            marker.addListener('click', () => {
                onSelectJob?.(job);
                const label = getStatusLabel(job.status, statusCatalog);
                const content = `
                  <div style="min-width:200px;font-family:system-ui,sans-serif;font-size:13px;line-height:1.45;">
                    <div style="font-weight:700;font-size:14px;margin-bottom:4px;">${job.service_number}</div>
                    <div style="color:#64748b;margin-bottom:6px;">${label}${
                        job.sla_state === 'breached'
                            ? ' · SLA BREACHED'
                            : job.sla_state === 'at_risk'
                              ? ' · SLA at risk'
                              : ''
                    }</div>
                    <div style="color:#334155;">${job.customer_name || ''}</div>
                    <div style="color:#94a3b8;font-size:12px;margin-top:4px;">${job.vendor_name || ''}</div>
                  </div>`;
                infoWindowRef.current.setContent(content);
                infoWindowRef.current.open({ map: mapRef.current, anchor: marker });
            });

            markersRef.current.push(marker);
        });

        driversOnMap.forEach((entry) => {
            const coords = normalizeCoords(entry.location);
            if (!coords) return;

            const stale = isLocationStale(entry.location?.recorded_at);
            const driverName = entry?.driver?.name || 'Driver';

            const marker = new google.maps.Marker({
                map: mapRef.current,
                position: { lat: coords.latitude, lng: coords.longitude },
                title: driverName,
                zIndex: 500,
                opacity: stale ? 0.5 : 1,
                icon: {
                    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
                    fillColor: stale ? '#94A3B8' : '#0F766E',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                    scale: 1.3,
                    anchor: new google.maps.Point(12, 22),
                },
            });

            marker.addListener('click', () => {
                const serviceLabel = entry?.current_service?.service_number || 'No active service';
                const recorded = entry.location?.recorded_at
                    ? new Date(entry.location.recorded_at).toLocaleString()
                    : '—';
                const content = `
                  <div style="min-width:180px;font-family:system-ui,sans-serif;font-size:13px;line-height:1.45;">
                    <div style="font-weight:700;">${driverName}</div>
                    <div style="color:#64748b;margin-top:4px;">Service: ${serviceLabel}</div>
                    <div style="color:#94a3b8;font-size:12px;margin-top:4px;">GPS: ${recorded}</div>
                  </div>`;
                infoWindowRef.current.setContent(content);
                infoWindowRef.current.open({ map: mapRef.current, anchor: marker });
            });

            markersRef.current.push(marker);
        });

        if (!selectedJobId && markersRef.current.length > 0 && !hasFitOnceRef.current) {
            fitToMarkers(72);
            hasFitOnceRef.current = true;
        }
    }, [
        jobsOnMap,
        driversOnMap,
        statusCatalog,
        selectedJobId,
        isLoaded,
        clearMarkers,
        onSelectJob,
        fitToMarkers,
    ]);

    useEffect(() => {
        if (!selectedJobId) return;
        const job = jobs.find((j) => j.id === selectedJobId);
        if (job) panToJob(job);
    }, [selectedJobId, jobs, panToJob]);

    if (loading) {
        return (
            <div
                className="flex items-center justify-center bg-slate-50 rounded-xl border border-gray-200"
                style={{ height: MAP_HEIGHT }}
            >
                <p className="text-sm text-gray-500">Loading map…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="flex items-center justify-center bg-red-50 rounded-xl border border-red-100"
                style={{ height: MAP_HEIGHT }}
            >
                <p className="text-sm text-red-600 px-4 text-center">Map unavailable: {error}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between gap-2 mb-2 px-1">
                <div className="flex items-center gap-3 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-blue-600" />
                        <strong className="text-gray-800">{jobsOnMap.length}</strong> job locations
                    </span>
                    <span className="text-gray-300">|</span>
                    <span className="inline-flex items-center gap-1">
                        <Truck className="w-3.5 h-3.5 text-teal-700" />
                        <strong className="text-gray-800">{driversOnMap.length}</strong> drivers
                    </span>
                </div>
                {jobs.length > jobsOnMap.length ? (
                    <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
                        {jobs.length - jobsOnMap.length} jobs missing map coordinates
                    </span>
                ) : null}
            </div>

            {selectedJob ? (
                <div className="mb-2 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs">
                    <span className="font-semibold text-gray-900 font-mono">{selectedJob.service_number}</span>
                    <span className="text-gray-500 mx-1">·</span>
                    <span className="text-gray-700">{selectedJob.customer_name}</span>
                    {!hasValidCoords(selectedJob.pickup_location) ? (
                        <span className="ml-2 text-amber-700">(no pickup coordinates)</span>
                    ) : null}
                </div>
            ) : (
                <p className="text-xs text-gray-400 mb-2 px-1">Select a job from the list to focus the map</p>
            )}

            <div className="relative flex-1 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                {jobsOnMap.length === 0 && driversOnMap.length === 0 ? (
                    <div
                        className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 text-center px-6"
                    >
                        <MapPin className="w-10 h-10 text-gray-300 mb-2" />
                        <p className="text-sm font-medium text-gray-700">No map coordinates available</p>
                        <p className="text-xs text-gray-400 mt-1 max-w-xs">
                            Active jobs are listed on the left, but none have pickup GPS data yet.
                        </p>
                    </div>
                ) : null}
                <div ref={mapContainerRef} style={{ width: '100%', height: MAP_HEIGHT }} />
                {legendItems.length > 0 ? (
                    <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur border border-gray-200 rounded-lg px-3 py-2 shadow-md">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            {legendItems.map((item) => (
                                <span
                                    key={item.code}
                                    className="inline-flex items-center gap-1.5 text-[11px] text-gray-600"
                                >
                                    <span
                                        className="w-2.5 h-2.5 rounded-full shrink-0"
                                        style={{ backgroundColor: item.color }}
                                    />
                                    {item.label}
                                </span>
                            ))}
                            <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                                <span className="w-2.5 h-2.5 rounded-full bg-teal-700 shrink-0" />
                                Driver GPS
                            </span>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default LiveJobsMap;
